import { Component, Input, OnInit } from '@angular/core';
import { ExifOrientation } from '../../../../../app.constants';
import { DynamicProperty, DYNAMIC_PROPERTY } from '../../../../../_objects/dynamic-property';
import { MarkItem } from '../../../../../_objects/mark';
import { OfflineReportPreviewService } from '../../../../../_services/offline-report-preview.service';
import { MarkOfflineService } from '../../../../../_services/offline/mark-offline.service';
import { ReportPreviewContentService } from '../../../../../_services/report-preview-content.service';
import { UtilsService } from '../../../../../_services/_utils.service';
import { ReportSummary } from '../../../../models/report-preview/report-summary';
import { TableColumn, TemplateSummaryTable, TemplateText } from '../../../../models/report-preview/report-template';
import { TableData } from '../../../../models/report-preview/TableData';

@Component({
    selector: 'app-report-summary-container-offline',
    templateUrl: './report-summary.component.html',
    styleUrls: ['./report-summary.component.scss'],
    standalone: false
})
export class ReportSummaryOfflineComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public summaryKey: string;
  @Input() public elementLocation: string;
  @Input() public isFirst = false;
  @Input() public tableData: TableData[];

  public reportSummary: ReportSummary;
  public table: TemplateSummaryTable;
  public rowSpans: number[][];
  public insertEmptyRows: number[];
  public summaryTableData: TableData[];

  rowHeight = 0;
  sizeUnit: string;
  trHeight: number;

  marks: MarkItem[];

  readonly reportSummaryStyle = 'position: absolute; display: table;';
  readonly textInlineStyle = 'display: table-cell; word-break: normal; white-space: pre-wrap; overflow: hidden;';
  readonly tableStyle = 'position: absolute; max-width: initial; max-height: initial; display: table; white-space: normal; overflow: hidden;'
   + ' border-spacing: 0mm 5mm; border-collapse: collapse; table-layout: fixed;';

  readonly summaryTrStyle = 'background-color: #fcf8e3; border-collapse: collapse;';
  readonly summaryThStyleBase = 'overflow: hidden; vertical-align: middle; text-align: center; padding: 0; border-collapse: collapse;';

  summaryThStyle(column: TableColumn) {
    if (!column.isHeaderVertical) {
      return this.summaryThStyleBase;
    } else {
      return this.summaryThStyleBase + 'transform: rotate(-90deg)';
    }
  }

  get trBorderStyle() {
    let width: string = null;
    let style: string = null;
    let color: string = null;

    if (this.reportSummary.tableBorderStyle) {
      const borderStyles = this.reportSummary.tableBorderStyle.split(' ');
      if (borderStyles.length > 2) {
        width = borderStyles[0];
        style = borderStyles[1];
        color = borderStyles[2];
      } else {
        style = this.reportSummary.tableBorderStyle;
      }
    }

    if (!this.utils.isNullOrUndefined(this.reportSummary.tableBorderWidth)
      && this.reportSummary.tableBorderWidth !== '') {
      width = this.reportSummary.tableBorderWidth;
    }

    if (!this.utils.isNullOrUndefined(this.reportSummary.tableBorderColor)
      && this.reportSummary.tableBorderColor !== '') {
      color = this.reportSummary.tableBorderColor;
    }

    return `border-top-width: ${width}; `
      + `border-top-style: ${style}; `
      + `border-top-color: ${color}; `
      + `border-bottom-width: ${width}; `
      + `border-bottom-style: ${style}; `
      + `border-bottom-color: ${color}; `
      + `border-left-width: ${width}; `
      + `border-left-style: ${style}; `
      + `border-left-color: ${color}; `
      + `border-right-width: ${width}; `
      + `border-right-style: ${style}; `
      + `border-right-color: ${color}; `;
  }

  get tableDataStyle () {
    let width: string = null;
    let style: string = null;
    let color: string = null;

    if (this.reportSummary.tableBorderStyle) {
      const borderStyles = this.reportSummary.tableBorderStyle.split(' ');
      if (borderStyles.length > 2) {
        width = borderStyles[0];
        style = borderStyles[1];
        color = borderStyles[2];
      } else {
        style = this.reportSummary.tableBorderStyle;
      }
    }

    if (!this.utils.isNullOrUndefined(this.reportSummary.tableBorderWidth)
      && this.reportSummary.tableBorderWidth !== '') {
      width = this.reportSummary.tableBorderWidth;
    }

    if (!this.utils.isNullOrUndefined(this.reportSummary.tableBorderColor)
      && this.reportSummary.tableBorderColor !== '') {
      color = this.reportSummary.tableBorderColor;
    }

    let str = '';
    if (this.reportSummary.tableHasBorderTop) {
      str += `border-top-width: ${width}; `
      + `border-top-style: ${style}; `
      + `border-top-color: ${color}; `;
    }
    if (this.reportSummary.tableHasBorderBottom) {
      str += `border-bottom-width: ${width}; `
      + `border-bottom-style: ${style}; `
      + `border-bottom-color: ${color}; `;
    }
    if (this.reportSummary.tableHasBorderLeft) {
      str += `border-left-width: ${width}; `
      + `border-left-style: ${style}; `
      + `border-left-color: ${color}; `;
    }
    if (this.reportSummary.tableHasBorderRight) {
      str += `border-right-width: ${width}; `
      + `border-right-style: ${style}; `
      + `border-right-color: ${color}; `;
    }
    return str;
  }

  constructor(
    private designService: OfflineReportPreviewService,
    private reportPreviewService: ReportPreviewContentService,
    private markService: MarkOfflineService,
    private utils: UtilsService
  ) {
  }

  get tableWidthNum() {
    return this.reportSummary.getTableWidthNum();
  }

  get tableHeightNum() {
    return this.reportSummary.getTableHeightNum();
  }

  get tableHeaderHeightNum() {
    return this.replaceNumForStr(this.reportSummary?.tableHeaderHeight);
  }

  get tableHeight() {
    return this.reportSummary.getHeightNum('table');
  }

  public ngOnInit() {
    this.table = this.designService.getPreviewPage(this.pageKey).getInspectionSummary().table;
    this.table.columns.forEach(column => {
      // 旧仕様のテンプレート補完対応
      if (column.textsInfo == null || column.textsInfo.length === 0) {
        column.textsInfo = [] as TemplateText[];
        column.texts.forEach(text => {
          column.textsInfo.push({
            text: text,
            type: 'dynamic',
            dynamicId: text
          } as TemplateText);
        });
      }
    });
    this.setSummaryTableData();
    this.markService.getMark().subscribe(marks => this.marks = marks.items);
    this.reportSummary = this.designService.getSummary(this.summaryKey);
    const tableDef = this.reportSummary.getTable();
    const tableHeaderHeight = tableDef.headerHeight;
    const pageBreakRow = this.reportSummary.defaultPageBreakRowCount;
    this.insertEmptyRows = new Array<number>((pageBreakRow - this.summaryTableData.length) >= 0 ? pageBreakRow - this.summaryTableData.length :0);
    this.rowHeight = (this.tableHeight - (tableHeaderHeight)) / pageBreakRow;
    this.sizeUnit = this.reportSummary.getSizeUnit() ?? 'mm';
    this.rowSpans = this.designService.setSpans(this.summaryTableData, this.reportSummary.tableColumns);
    this.trHeight = 0.0;
  }

  public onClick(event: MouseEvent) {
  }

  getColumnWidth(column: TableColumn) {
    const tableWidth = Number(this.reportSummary.tableWidth);
    return tableWidth * (column.widthRatio / 100);
  }

  public getTableColumnVerticalStyle(columnHight: number, columnWidth: number) {
    // divを突き抜ける原因はheightを設定していないから。( height: {width}{sizeUnit}; )
    // しかし、heightをするとvertical-align: middle;が意味をなさなくなる。
    if (columnWidth / 2 > columnHight / 2) {
      return this.getTransformStyle((columnHight / 2) - (columnWidth / 2));
    }

    return this.getTransformStyle((columnWidth / 2) - (columnHight / 2));
  }

  public getTransformStyle(x: number) {
    const styles = ['transform', '-moz-transform', '-webkit-transform', '-o-transform', '-ms-transform'];
    return styles
      .map(style => style + `translateX(${x}${this.sizeUnit}) rotate(-90deg)`)
      .join(' ');
  }

  getTableRowStyle(columnNo: number) {
    let result = '';
    if (this.reportSummary.tableHasRowBackground
        && this.reportSummary.tableRowBackgroundColor
        && columnNo % 2 === 1) {
      result += `background-color: ${this.reportSummary.tableRowBackgroundColor};`;
    }

    return result;
  }

  getTextContent(column: TableColumn) {
    return this.reportPreviewService.getColumnHeaderContent(column, this.marks);
  }

  replaceNumForStr(str: string) {
    if (!str) {
      return 0;
    }

    return Number(str.replace(/[^0-9]/g, ''));
  }

  public calcTrHeight() {
    const pageBreakRow = this.reportSummary.defaultPageBreakRowCount;
    return this.tableHeightNum != 0
      ? (this.tableHeightNum - this.tableHeaderHeightNum) / pageBreakRow
      : ((this.tableHeight - this.tableHeaderHeightNum) / pageBreakRow);
  }

  public calcTrHeightForColumn(tableColumn: TableColumn, tableData: TableData) {
    const pageBreakRow = this.reportSummary.defaultPageBreakRowCount;
    const baseTrHeight = this.calcTrHeight();

    tableColumn.textsInfo.forEach(_ => {
      const property = new DynamicProperty(_.dynamicId);
      if (property.key == DYNAMIC_PROPERTY.KEY.InspectionPicturesImage) {
        this.trHeight = 0.0;
        tableData.images.forEach(image => {
          let width = image.width != 0 ? image.width : this.tableWidthNum * (tableColumn.widthRatio / 100);
          let height = image.height != 0 ? image.height : (this.tableHeight  - this.tableHeaderHeightNum) / pageBreakRow;
          let maxHeight = baseTrHeight;

          // ExifOrientation
          switch (image.orientation) {
            case ExifOrientation.Default:
            case ExifOrientation.MirrorHorizontal:
            case ExifOrientation.Rotate180:
            case ExifOrientation.MirrorVertical:
            default:
              break;
            case ExifOrientation.MirrorHorizontalAndRotate270:
            case ExifOrientation.Rotate90:
            case ExifOrientation.MirrorHorizontalAndRotate90:
            case ExifOrientation.Rotate270:
              const tempWidth = width;
              width = height;
              height = tempWidth;
              break;
          }

          if (height > width) {
            maxHeight *= 2;
          }

          this.trHeight += maxHeight;
        });
      }
    });

    return this.trHeight === 0
      ? baseTrHeight
      : this.trHeight;
  }

  public isBackgroundColorStyle(tableColumn: TableColumn) {
    return tableColumn.textsInfo.some(_ => {
      const property = new DynamicProperty(_.dynamicId);
      return property.key == DYNAMIC_PROPERTY.KEY.InspectionSummaryClass || property.key == DYNAMIC_PROPERTY.KEY.InspectionPicturesClass;
    });
  }

  getMarginLeft(column: TableColumn) {
    const tableHeaderHeight = this.reportSummary.tableHeaderHeightNum;
    const width =  this.tableWidthNum * (column.widthRatio / 100);
    if (width / 2 > tableHeaderHeight / 2) {
      const x = (tableHeaderHeight / 2) - (width / 2);
      return `${x}${this.sizeUnit}`;
    } else {
      const x = (width / 2) - (tableHeaderHeight / 2);
      return `${x}${this.sizeUnit}`;
    }
  }

  setSummaryTableData() {
    this.summaryTableData = [];
    this.tableData.forEach(data =>{
      this.summaryTableData.push(data);
    });
  }
}
